/**
 * NotificationBell — Phase 18b.
 *
 * Topbar bell with:
 *   * Unread count badge
 *   * Dropdown showing the 5 most recent notifications
 *   * "Mark all read" action
 *   * "View all" link to /notifications
 *   * Small green dot when the WebSocket is live (Phase 18a)
 *
 * Hooks into the existing ``useNotifications`` hook for Redux
 * state + ``useNotificationSocket`` for real-time deliveries.
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FiBell, FiCheckCircle, FiCalendar, FiFileText,
  FiShield, FiBox, FiStar, FiExternalLink,
} from 'react-icons/fi';
import { FaRupeeSign } from 'react-icons/fa';
import { Badge, Dropdown, DropdownMenu, DropdownToggle, Button } from 'reactstrap';
import useNotifications from '../../hooks/useNotifications';
import useNotificationSocket from '../../hooks/useNotificationSocket';
import { useTranslation } from '../../contexts/LanguageContext';

// Human-readable "time ago" without adding a dep. Close enough
// for a dropdown preview; the full inbox uses a real date lib.
const timeAgo = (iso) => {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(iso).toLocaleDateString();
};

const PRIORITY_COLOR = {
  URGENT: 'danger',
  HIGH: 'warning',
  NORMAL: 'info',
  LOW: 'secondary',
};

// ── Category derivation (mirrors NotificationCenter) ─────────────
const CATEGORY_META = {
  booking:     { icon: FiCalendar,    color: '#7C3AED' },
  contract:    { icon: FiFileText,    color: '#2563eb' },
  billing:     { icon: FaRupeeSign,  color: '#10b981' },
  compliance:  { icon: FiShield,      color: '#dc2626' },
  operations:  { icon: FiBox,         color: '#f59e0b' },
  marketplace: { icon: FiStar,        color: '#06b6d4' },
  system:      { icon: FiBell,        color: '#6b7280' },
};

const eventToCategory = (eventType) => {
  if (!eventType) return 'system';
  if (eventType.startsWith('booking.') || eventType.startsWith('offer.')) return 'booking';
  if (eventType.startsWith('contract.')) return 'contract';
  if (eventType.startsWith('invoice.')
    || eventType.startsWith('payment.')
    || eventType.startsWith('payout.')
    || eventType === 'dunning.reminder_due') return 'billing';
  if (eventType.startsWith('compliance.')
    || eventType.startsWith('dispute.')
    || eventType.startsWith('kyc.')) return 'compliance';
  if (eventType.startsWith('damage.')
    || eventType.startsWith('inventory.')
    || eventType.startsWith('logistics.')
    || eventType.startsWith('grn.')) return 'operations';
  if (eventType.startsWith('listing.') || eventType.startsWith('trust.')) return 'marketplace';
  return 'system';
};

const deriveLink = (n) => {
  const d = n.template_data || {};
  if (d.invoice_id) return `/billing/invoices/${d.invoice_id}`;
  if (d.contract_id) return `/contracts/${d.contract_id}`;
  if (d.booking_id) return `/bookings/${d.booking_id}`;
  if (d.dispute_id) return `/disputes/${d.dispute_id}`;
  if (d.listing_id) return `/warehouses/${d.listing_id}`;
  return n.link || '/notifications';
};

const NotificationBell = () => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    notifications, unreadCount, loadNotifications, markAllAsRead,
  } = useNotifications();

  // Keep the dropdown preview in sync on mount + when a new WS
  // event pushes. Load a small slice (first page, 10 rows) —
  // enough to render the 5-row preview without a separate endpoint.
  useEffect(() => {
    loadNotifications({ page: 1, pageSize: 10 });
  }, [loadNotifications]);

  // Phase 18a — live socket. Re-fetch on each arrival so the
  // Redux slice (not just the count) is in sync; the real-time
  // payload is also dispatched via the toast surface (Phase 18c).
  const { connected } = useNotificationSocket({
    onNotification: () => {
      loadNotifications({ page: 1, pageSize: 10 });
    },
  });

  const preview = (notifications || []).slice(0, 5);

  return (
    <Dropdown isOpen={open} toggle={() => setOpen((o) => !o)}>
      <DropdownToggle
        tag="button"
        className="btn btn-link p-2 position-relative"
        style={{ border: 'none', background: 'transparent' }}
        aria-label={t('bell.title', 'Notifications')}
      >
        <FiBell size={20} />
        {unreadCount > 0 && (
          <Badge
            color="danger"
            pill
            className="position-absolute"
            style={{
              top: 4, right: 2,
              fontSize: '0.65rem', padding: '0.2em 0.45em',
              minWidth: 18,
            }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </Badge>
        )}
        {/* Real-time indicator — a small green dot when the WS is
            connected. Absent when reconnecting or offline. */}
        {connected && (
          <span
            className="position-absolute"
            style={{
              bottom: 6, right: 6, width: 8, height: 8,
              background: '#28a745', borderRadius: '50%',
              border: '2px solid #fff',
            }}
            title={t('bell.connected', 'Live')}
          />
        )}
      </DropdownToggle>

      <DropdownMenu
        end
        style={{
          width: 380, padding: 0, maxHeight: 520, overflowY: 'auto',
          borderRadius: 12, border: '1px solid #e5e7eb',
          boxShadow: '0 10px 25px rgba(0,0,0,0.08)',
        }}
      >
        <div className="d-flex justify-content-between align-items-center px-3 py-3"
          style={{ borderBottom: '1px solid #f3f4f6' }}>
          <div className="d-flex align-items-center" style={{ gap: 8 }}>
            <strong style={{ fontSize: 14 }}>
              {t('bell.title', 'Notifications')}
            </strong>
            {unreadCount > 0 && (
              <Badge color="danger" pill style={{ fontSize: 10, fontWeight: 700 }}>
                {unreadCount}
              </Badge>
            )}
          </div>
          {unreadCount > 0 && (
            <Button
              color="link" size="sm" className="p-0 text-decoration-none"
              style={{ fontSize: 12 }}
              onClick={() => markAllAsRead()}
            >
              <FiCheckCircle size={12} className="me-1" />
              {t('bell.markAllRead', 'Mark all read')}
            </Button>
          )}
        </div>

        {preview.length === 0 ? (
          <div className="text-center text-muted py-4">
            <FiBell size={24} className="mb-2 text-muted" />
            <div className="small">{t('bell.empty', 'No notifications yet')}</div>
          </div>
        ) : (
          <div>
            {preview.map((n) => {
              const unread = !n.read_at && !n.is_read;
              const eventType = n.template_data?.event_type || '';
              const category = eventToCategory(eventType);
              const meta = CATEGORY_META[category];
              const Icon = meta.icon;
              const link = deriveLink(n);
              return (
                <div
                  key={n.id}
                  role="button"
                  onClick={() => { setOpen(false); navigate(link); }}
                  style={{
                    display: 'flex', alignItems: 'flex-start', gap: 10,
                    padding: '12px 14px',
                    borderBottom: '1px solid #f3f4f6',
                    background: unread ? `${meta.color}08` : '#fff',
                    borderLeft: unread
                      ? `3px solid ${meta.color}`
                      : '3px solid transparent',
                    cursor: 'pointer',
                    transition: 'background 0.12s ease',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = `${meta.color}12`; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = unread ? `${meta.color}08` : '#fff'; }}
                >
                  <div style={{
                    width: 30, height: 30, borderRadius: 8,
                    background: `${meta.color}15`, color: meta.color,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <Icon size={14} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="d-flex justify-content-between align-items-start" style={{ gap: 6 }}>
                      <div className="fw-semibold text-truncate"
                        style={{
                          fontSize: 13,
                          color: unread ? '#111827' : '#6b7280',
                          flex: 1, minWidth: 0,
                        }}>
                        {n.subject || n.title || n.body?.slice(0, 60)}
                      </div>
                      {n.priority && n.priority !== 'NORMAL' && (
                        <Badge color={PRIORITY_COLOR[n.priority] || 'secondary'}
                          pill style={{ fontSize: 9, fontWeight: 700, flexShrink: 0 }}>
                          {n.priority}
                        </Badge>
                      )}
                    </div>
                    <div className="text-muted small text-truncate"
                      style={{ fontSize: 12 }}>
                      {n.body}
                    </div>
                    <div className="small text-muted" style={{ fontSize: 11, marginTop: 2 }}>
                      {timeAgo(n.created_at)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="py-2 text-center"
          style={{ borderTop: '1px solid #f3f4f6', background: '#fafafa', borderRadius: '0 0 12px 12px' }}>
          <Link
            to="/notifications"
            className="small text-decoration-none d-inline-flex align-items-center"
            style={{ fontSize: 12, fontWeight: 600, gap: 4 }}
            onClick={() => setOpen(false)}
          >
            {t('bell.viewAll', 'View all notifications')}
            <FiExternalLink size={11} />
          </Link>
        </div>
      </DropdownMenu>
    </Dropdown>
  );
};

export default NotificationBell;
